// frontend/src/utils/asyncExport.js
//
// The client half of the async export rail.
//
// An export endpoint answers one of two ways. Up to the server's row
// threshold the response IS the file. Past it the server answers `202` with an
// ExportJob, generates the file on the pipeline rail, and the merchant
// downloads it afterwards (backend: api/views/viewsets.py ExportMixin.export,
// core/models.py ExportJob).
//
// A caller that only understood `res.ok` would hand `downloadBlobResponse` a
// JSON job envelope and save it under the export's file name: a file that
// downloads, opens, and is not the export. So this owns the whole "an export
// was asked for; produce a downloadable Response or throw" contract, and the
// caller (components/common/GridExportButton.jsx) keeps only its fetcher and
// its copy.

/** Milliseconds between polls of a generating job. */
const POLL_INTERVAL_MS = 2000;

/**
 * How long to keep polling before giving up.
 *
 * Reaching it does NOT mean the export failed: the pipeline run outlives this
 * page and the file lands in Exports, so the caller says that rather than
 * reporting a failure.
 */
const POLL_TIMEOUT_MS = 20 * 60 * 1000;

export class ExportStillGenerating extends Error {
  constructor(job) {
    super('Export is still generating');
    this.name = 'ExportStillGenerating';
    this.job = job;
  }
}

/**
 * The caller stopped caring — its page unmounted mid-poll.
 *
 * Distinct from both other outcomes because it describes the CLIENT, not the
 * export: nothing failed and nothing timed out, so there is nobody to tell
 * and nothing to say. Callers throw it from their `getJob` to break the loop.
 */
export class ExportAbandoned extends Error {
  constructor() {
    super('Export polling abandoned');
    this.name = 'ExportAbandoned';
  }
}

export class ExportGenerationFailed extends Error {
  constructor(job) {
    super('Export generation failed');
    this.name = 'ExportGenerationFailed';
    this.job = job;
  }
}

/**
 * Is this response a job envelope rather than a file?
 *
 * Read off the status code, not the body: a 202 is the server saying "not
 * this response", and sniffing the content type would make an export whose
 * rows happened to serialise oddly look like a job.
 */
export const isAsyncExport = (res) => res?.status === 202;

/**
 * Wait for a queued export to be generated, then fetch the file.
 *
 * @param {object}   job      the 202's body — needs `id`.
 * @param {object}   io
 * @param {Function} io.getJob      (id) => Promise<job>       poll one job
 * @param {Function} io.getFile     (id) => Promise<Response>  download it
 * @param {Function} [io.sleep]     (ms) => Promise            injectable for tests
 * @param {Function} [io.now]       () => number               injectable for tests
 * @returns {Promise<{response: Response, job: object}>} the file, and the
 *   READY job it came from. The job rides along because the caller cannot
 *   name the file without it: the server dated the name at generation time
 *   (`ExportJob.filename`), and a caller naming it from its own clock would
 *   stamp today's date on a file generated days ago.
 * @throws {ExportGenerationFailed} the run failed — there is no file, and
 *   there will not be one.
 * @throws {ExportStillGenerating} we stopped waiting. The run continues, and
 *   the file will be waiting in the exports list.
 */
export async function awaitExportFile(job, { getJob, getFile, sleep = defaultSleep, now = Date.now }) {
  const deadline = now() + POLL_TIMEOUT_MS;
  let current = job;
  while (!current.is_ready) {
    if (current.status === 'failed' || current.status === 'partial') {
      // A failed generation is final: the export could not be produced from
      // what the merchant asked for.
      throw new ExportGenerationFailed(current);
    }
    if (now() >= deadline) throw new ExportStillGenerating(current);
    await sleep(POLL_INTERVAL_MS);
    current = await getJob(job.id);
  }
  return { response: await getFile(current.id), job: current };
}

const defaultSleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });
